import produce from 'immer'
import React, { FC, useEffect, useRef, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { NextOfKinErrorModel } from '../../models/applicationRegistration/compensation/nextOfKinErrorModel'
import { NextOfKinModel } from '../../models/applicationRegistration/compensation/nextOfKinModel'
import { FileRegistrationModel } from '../../models/applicationRegistration/fileRegistration/fileRegistrationModel'
import { ValidationObjectModel } from '../../models/common/validationObjectModel'
import common from '../../services/applicationRegistration/common'
import metaDataService from '../../services/metaDataService'
import { changeLoadingRequest } from '../../store/app/actionTypes'
import {
  ALERT_WARNING,
  TOAST_POSITION_TOP_RIGHT,
  TOAST_TRANSITION_SLIDE,
} from '../../utils/constants'
import { validationError } from '../../utils/errorMessages'
import { showTotast } from '../../utils/toast'
import {
  getOldAndNewNicFromGivenNic,
  handleNotification,
  validateForm,
} from '../../utils/utilFunctions'
import InvalidFeedback from '../common/invalidFeedback/InvalidFeedback'
import LoadingComponent from '../common/loading/LoadingComponent'

export interface AddNextOfKinModalProps {
  fileRegistrationObject: FileRegistrationModel
  onCancel: () => void
  onSuccess: () => void
}

const AddNextOfKinModal: FC<AddNextOfKinModalProps> = ({
  fileRegistrationObject,
  onCancel,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [nextOfKin, setNextOfKin] = useState({
    applicationFileId: fileRegistrationObject.id,
    newNic: true,
  } as NextOfKinModel)
  const [relationshipList, setRelationshipList] = useState([])
  const [bankList, setBankList] = useState([])
  const [branchList, setBranchList] = useState(
    [] as { id: number; bankId: number; name: string }[]
  )
  const [errors, setErrors] = useState({} as NextOfKinErrorModel)
  const latestBankId = useRef(0)
  const dispatch = useDispatch()

  useEffect(() => {
    async function getMetaData() {
      const relationshipData = await metaDataService.getRelationshipList()
      if (relationshipData.status == 200) {
        setRelationshipList(relationshipData.data)
      }
      const bankData = await metaDataService.getBankList()
      if (bankData.status == 200) {
        setBankList(bankData.data)
      }
      setLoading(false)
    }
    getMetaData()
  }, [])

  const getBranchesList = async (id: number) => {
    latestBankId.current = id
    setBranchList([])
    const branchData = await metaDataService.getBranchesList(id)
    if (latestBankId.current !== id) return
    setBranchList(branchData.status == 200 ? branchData.data : [])
  }

  const nicValidation = (details: NextOfKinModel) =>
    ({
      type: 'text',
      value: details.newNic ? details.newNicNumber : details.oldNicNumber,
      section: 'Nic Number',
      maxLength: details.newNic ? 12 : 10,
      minLength: details.newNic ? 12 : 10,
    } as ValidationObjectModel)

  const findErrors = () => {
    const newErrors = {} as NextOfKinErrorModel
    const nameError = validateForm({
      type: 'text',
      value: nextOfKin.name,
      section: 'Name',
      maxLength: 50,
    } as ValidationObjectModel)
    if (nameError) newErrors.nameError = nameError

    const nicError = validateForm(nicValidation(nextOfKin))
    if (nicError) newErrors.nicError = nicError

    const permanentAddressError = validateForm({
      type: 'text',
      value: nextOfKin.permanentAddress,
      section: 'Permanent Address',
      maxLength: 255,
    } as ValidationObjectModel)
    if (permanentAddressError)
      newErrors.permanentAddressError = permanentAddressError

    const relationshipError = validateForm({
      type: 'text',
      value: nextOfKin.relationship,
      section: 'Relationship',
    } as ValidationObjectModel)
    if (relationshipError) newErrors.relationshipError = relationshipError

    const bankIdError = validateForm({
      type: 'number',
      value: nextOfKin.bankId,
      section: 'Bank Name',
      minValue: 1,
      customMessage: 'Bank Name cannot be empty !',
    } as ValidationObjectModel)
    if (bankIdError) newErrors.bankIdError = bankIdError

    const branchIdError = validateForm({
      type: 'number',
      value: nextOfKin.branchId,
      section: 'Branch Name',
      minValue: 1,
      customMessage: 'Branch Name cannot be empty !',
    } as ValidationObjectModel)
    if (branchIdError) newErrors.branchIdError = branchIdError

    const accountNumberError = validateForm({
      type: 'text',
      value: nextOfKin.accountNumber,
      section: 'Account Number',
      maxLength: 15,
    } as ValidationObjectModel)
    if (accountNumberError) newErrors.accountNumberError = accountNumberError

    return newErrors
  }

  const handleSave = async () => {
    if (saving) return
    const foundErrors = findErrors()
    if (Object.keys(foundErrors).length > 0) {
      showTotast(
        ALERT_WARNING,
        validationError,
        TOAST_POSITION_TOP_RIGHT,
        5,
        TOAST_TRANSITION_SLIDE
      )
      setErrors(foundErrors)
      return
    }
    setSaving(true)
    dispatch(changeLoadingRequest())
    const result = await common.apiAddDraftedNextOfKin(
      fileRegistrationObject.id,
      fileRegistrationObject.scheme.toLowerCase(),
      nextOfKin
    )
    dispatch(changeLoadingRequest())
    setSaving(false)
    handleNotification(result, 'Next of Kin and Dependent Added Successfully')
    if (result.status === 200) {
      onSuccess()
    }
  }

  const updateField = (
    update: (draft: NextOfKinModel) => void,
    errorKey?: keyof NextOfKinErrorModel
  ) => {
    setNextOfKin((current) => produce(current, update))
    if (errorKey) {
      setErrors((current) =>
        produce(current, (draft) => {
          draft[errorKey] = ''
        })
      )
    }
  }

  return (
    <Modal
      size="lg"
      show={true}
      onHide={onCancel}
      dialogClassName="custom-modal-layout"
      contentClassName="custom-modal-content"
    >
      <Modal.Header
        closeButton
        closeVariant="white"
        className="custom-modal-header"
      >
        <Modal.Title>Add Next of Kin</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="loading-layout">
            <LoadingComponent />
          </div>
        ) : (
          <Form>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="addNokName">
                <Form.Label>
                  Name <span className="required-field-astrix">*</span>
                </Form.Label>
                <Form.Control
                  isInvalid={!!errors.nameError}
                  type="text"
                  maxLength={50}
                  placeholder="Name"
                  value={nextOfKin.name ?? ''}
                  onChange={(e) =>
                    updateField((draft) => {
                      draft.name = e.target.value
                    }, 'nameError')
                  }
                />
                <InvalidFeedback message={errors.nameError} />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="addNokNic">
                <Form.Label>
                  NIC Number <span className="required-field-astrix">*</span>
                </Form.Label>
                <Row>
                  <Col sm={1}>
                    <Form.Check
                      inline
                      name="addNokNicType"
                      label="New"
                      type="radio"
                      id="addNokNicType-N"
                      checked={nextOfKin.newNic}
                      onChange={() =>
                        updateField((draft) => {
                          draft.newNic = true
                        }, 'nicError')
                      }
                    />
                  </Col>
                  <Col sm={1}>
                    <Form.Check
                      inline
                      name="addNokNicType"
                      label="Old"
                      type="radio"
                      id="addNokNicType-O"
                      checked={!nextOfKin.newNic}
                      onChange={() =>
                        updateField((draft) => {
                          draft.newNic = false
                        }, 'nicError')
                      }
                    />
                  </Col>
                </Row>
                <Form.Control
                  isInvalid={!!errors.nicError}
                  type="text"
                  placeholder="NIC Number"
                  value={
                    (nextOfKin.newNic
                      ? nextOfKin.newNicNumber
                      : nextOfKin.oldNicNumber) ?? ''
                  }
                  onBlur={async (e) => {
                    const identity = await getOldAndNewNicFromGivenNic(
                      fileRegistrationObject.incidentDate
                        ? new Date(fileRegistrationObject.incidentDate * 1000)
                        : new Date(),
                      e.target.value,
                      nextOfKin.newNic ? 'N' : 'O'
                    )
                    const newDetails = produce(
                      nextOfKin,
                      (draft: NextOfKinModel) => {
                        draft.newNicNumber = identity.newNic
                        draft.oldNicNumber = identity.oldNic
                      }
                    )
                    setNextOfKin(newDetails)
                    setErrors(
                      produce(errors, (draft) => {
                        draft.nicError = validateForm(nicValidation(newDetails))
                      })
                    )
                  }}
                  onChange={(e) =>
                    updateField((draft) => {
                      if (draft.newNic) {
                        draft.newNicNumber = e.target.value
                      } else {
                        draft.oldNicNumber = e.target.value
                      }
                    }, 'nicError')
                  }
                />
                <InvalidFeedback message={errors.nicError} />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="addNokIdNumberRemark">
                <Form.Label>ID Number Remark</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={45}
                  placeholder="ID Number Remark"
                  value={nextOfKin.idNumberRemark ?? ''}
                  onChange={(e) =>
                    updateField((draft) => {
                      draft.idNumberRemark = e.target.value
                    })
                  }
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="addNokAddress">
                <Form.Label>
                  Permanent Address{' '}
                  <span className="required-field-astrix">*</span>
                </Form.Label>
                <Form.Control
                  isInvalid={!!errors.permanentAddressError}
                  type="text"
                  maxLength={255}
                  placeholder="Permanent Address"
                  value={nextOfKin.permanentAddress ?? ''}
                  onChange={(e) =>
                    updateField((draft) => {
                      draft.permanentAddress = e.target.value
                    }, 'permanentAddressError')
                  }
                />
                <InvalidFeedback message={errors.permanentAddressError} />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="addNokRelationship">
                <Form.Label>
                  Relationship <span className="required-field-astrix">*</span>
                </Form.Label>
                <Form.Control
                  isInvalid={!!errors.relationshipError}
                  className="form-select"
                  as="select"
                  value={nextOfKin.relationship ?? ''}
                  onChange={(e) =>
                    updateField((draft) => {
                      draft.relationship = e.target.value
                    }, 'relationshipError')
                  }
                >
                  <option value="">--Select Relationship--</option>
                  {relationshipList.map(
                    (relationship: { name: string; id: number }) => (
                      <option
                        key={`relationship-${relationship.id}`}
                        value={relationship.name}
                      >
                        {relationship.name}
                      </option>
                    )
                  )}
                </Form.Control>
                <InvalidFeedback message={errors.relationshipError} />
              </Form.Group>
            </Row>
            <Col sm={12} className="border-label mt-3 mb-3">
              <div className="border-label-span">Bank Details</div>
              <Row>
                <Form.Group as={Col} controlId="addNokBank">
                  <Form.Label>
                    Bank Name <span className="required-field-astrix">*</span>
                  </Form.Label>
                  <Form.Control
                    isInvalid={!!errors.bankIdError}
                    className="form-select"
                    as="select"
                    value={nextOfKin.bankId ?? 0}
                    onChange={(e) => {
                      updateField((draft) => {
                        draft.bankId = parseInt(e.target.value)
                        draft.branchId = 0
                      }, 'bankIdError')
                      getBranchesList(parseInt(e.target.value))
                    }}
                  >
                    {!nextOfKin.bankId && (
                      <option value="0">--Select Bank--</option>
                    )}
                    {bankList.map((bank: { name: string; id: number }) => (
                      <option key={`bank-${bank.id}`} value={bank.id}>
                        {bank.name}
                      </option>
                    ))}
                  </Form.Control>
                  <InvalidFeedback message={errors.bankIdError} />
                </Form.Group>
                <Form.Group as={Col} controlId="addNokBranch">
                  <Form.Label>
                    Branch Name <span className="required-field-astrix">*</span>
                  </Form.Label>
                  <Form.Control
                    isInvalid={!!errors.branchIdError}
                    className="form-select"
                    as="select"
                    disabled={!nextOfKin.bankId}
                    value={nextOfKin.branchId ?? 0}
                    onChange={(e) =>
                      updateField((draft) => {
                        draft.branchId = parseInt(e.target.value)
                      }, 'branchIdError')
                    }
                  >
                    {!nextOfKin.branchId && (
                      <option value="0">--Select Branch--</option>
                    )}
                    {branchList
                      .filter((branch) => branch.bankId === nextOfKin.bankId)
                      .map((branch) => (
                        <option key={`branch-${branch.id}`} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                  </Form.Control>
                  <InvalidFeedback message={errors.branchIdError} />
                </Form.Group>
                <Form.Group as={Col} controlId="addNokAccountNumber">
                  <Form.Label>
                    Account Number{' '}
                    <span className="required-field-astrix">*</span>
                  </Form.Label>
                  <Form.Control
                    isInvalid={!!errors.accountNumberError}
                    type="text"
                    maxLength={15}
                    placeholder="Account Number"
                    value={nextOfKin.accountNumber ?? ''}
                    onChange={(e) =>
                      updateField((draft) => {
                        draft.accountNumber = e.target.value
                      }, 'accountNumberError')
                    }
                  />
                  <InvalidFeedback message={errors.accountNumberError} />
                </Form.Group>
              </Row>
            </Col>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="save-button"
          disabled={saving || loading}
          onClick={handleSave}
        >
          Add
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddNextOfKinModal
